// HoldAtWaypointModal — Garmin-style hold-at-fix dialog.
//
// Lets the pilot add a hold to any waypoint in the active flight plan
// with the same parameters a GTN/G1000 would expose:
//   - Hold direction (turn-and-hold orientation, e.g. NE, E, SE, ...)
//   - Inbound course (magnetic degrees)
//   - Turn direction (R = standard right, L = non-standard left)
//   - Leg length (time in minutes OR distance in NM)
//
// On accept, we mutate the chosen route point so its procedureTag carries
// the hold parameters and the `manuallyAdded` flag is true.
// PlanProceduresLayer reads that flag and ALWAYS renders manually-added
// holds, bypassing the AIM-5-3-8 direct-entry-sector filter that drops
// auto-extracted HILPTs when course reversal isn't required.
//
// This is a foundation for future ATC "hold as published" / "hold for
// release" workflows: same UI, same data path, different entry point.

import { useEffect, useState } from 'react';
import { usePlanStore } from '../stores/planStore';
import type { ExtendedPlanPoint, ProcedureTag } from '../lib/procedureExpansion';

interface Props {
  /** Route index of the waypoint to attach a hold to. */
  routeIndex: number;
  onClose: () => void;
}

type LegMode = 'time' | 'distance';

const DIRECTION_OPTIONS = [
  { value: 'N',  label: 'North' },
  { value: 'NE', label: 'Northeast' },
  { value: 'E',  label: 'East' },
  { value: 'SE', label: 'Southeast' },
  { value: 'S',  label: 'South' },
  { value: 'SW', label: 'Southwest' },
  { value: 'W',  label: 'West' },
  { value: 'NW', label: 'Northwest' },
];

/**
 * Suggest a default inbound course from the hold direction. The inbound
 * course points TOWARD the fix from the hold's location. A hold to the
 * 'NE' means the holding pattern body sits northeast of the fix; the
 * inbound course points from NE-back-to-fix, i.e. southwest (225°).
 */
function directionToInboundCourse(dir: string): number {
  switch (dir) {
    case 'N':  return 180;
    case 'NE': return 225;
    case 'E':  return 270;
    case 'SE': return 315;
    case 'S':  return 360;
    case 'SW': return 45;
    case 'W':  return 90;
    case 'NW': return 135;
    default:   return 180;
  }
}

export function HoldAtWaypointModal({ routeIndex, onClose }: Props) {
  const route = usePlanStore((s) => s.route);
  const replaceAt = usePlanStore((s) => s.replaceAt);
  const point = route[routeIndex] as ExtendedPlanPoint | undefined;

  const [direction, setDirection] = useState('NE');
  const [inboundCourse, setInboundCourse] = useState(225);
  const [turnDirection, setTurnDirection] = useState<'L' | 'R'>('R');
  const [legMode, setLegMode] = useState<LegMode>('time');
  const [legMinutes, setLegMinutes] = useState(1);
  const [legNm, setLegNm] = useState(5);

  // When the pilot changes the direction picker, propose a matching
  // inbound course. They can still override it manually.
  useEffect(() => {
    setInboundCourse(directionToInboundCourse(direction));
  }, [direction]);

  if (!point) return null;

  function commit() {
    if (!point) return;
    const existing: ProcedureTag = point.procedureTag ?? {
      airportIcao: '',
      procIdent: 'CUSTOM',
      routeType: 'IAP',
      transIdent: '',
      legType: 'HM',
      seq: 0,
      placeholder: false,
      altitude: null,
    };
    const holdLeg = legMode === 'time'
      ? { kind: 'time' as const, minutes: legMinutes }
      : { kind: 'distance' as const, nm: legNm };
    const tag: ProcedureTag = {
      ...existing,
      legType: 'HM',
      isHoldingPattern: true,
      holdInboundCourse: inboundCourse,
      holdLeg,
      holdTurn: turnDirection,
      manuallyAdded: true,
    };
    const next: ExtendedPlanPoint = { ...point, procedureTag: tag };
    replaceAt(routeIndex, next);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70"
      onClick={onClose}
      role="dialog"
      aria-label="Add hold at waypoint"
    >
      <div
        className="bg-cockpit-800 border border-cockpit-700 rounded-lg p-4 w-[90%] max-w-md space-y-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <h2 className="text-base font-bold text-cockpit-100">
            Hold at {point.ident}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-cockpit-300 hover:text-cockpit-100"
            aria-label="Close"
          >×</button>
        </header>

        <div className="space-y-2">
          <label className="block text-xs text-cockpit-300">
            Hold direction
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              className="mt-0.5 w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5"
            >
              {DIRECTION_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.value} — {d.label}</option>
              ))}
            </select>
          </label>

          <label className="block text-xs text-cockpit-300">
            Inbound course (magnetic, deg)
            <input
              type="number"
              min={0}
              max={359}
              value={inboundCourse}
              onChange={(e) => setInboundCourse(Math.max(0, Math.min(359, Number(e.target.value) || 0)))}
              className="mt-0.5 w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5 font-mono"
            />
          </label>

          <div className="flex items-center gap-3 text-xs text-cockpit-300">
            <span>Turns</span>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={turnDirection === 'R'}
                onChange={() => setTurnDirection('R')}
              />
              Right (standard)
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={turnDirection === 'L'}
                onChange={() => setTurnDirection('L')}
              />
              Left
            </label>
          </div>

          <div className="flex items-center gap-3 text-xs text-cockpit-300">
            <span>Leg length</span>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={legMode === 'time'}
                onChange={() => setLegMode('time')}
              />
              Time
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={legMode === 'distance'}
                onChange={() => setLegMode('distance')}
              />
              Distance
            </label>
          </div>

          {legMode === 'time' ? (
            <label className="block text-xs text-cockpit-300">
              Outbound leg (minutes)
              <input
                type="number"
                step={0.5}
                min={0.5}
                max={10}
                value={legMinutes}
                onChange={(e) => setLegMinutes(Number(e.target.value) || 1)}
                className="mt-0.5 w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5 font-mono"
              />
            </label>
          ) : (
            <label className="block text-xs text-cockpit-300">
              Outbound DME leg (NM)
              <input
                type="number"
                step={0.5}
                min={0.5}
                max={50}
                value={legNm}
                onChange={(e) => setLegNm(Number(e.target.value) || 5)}
                className="mt-0.5 w-full bg-cockpit-700 border border-cockpit-600 rounded-md text-sm px-2 py-1.5 font-mono"
              />
            </label>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-cockpit-200 hover:text-cockpit-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={commit}
            className="px-3 py-1.5 text-sm rounded-md bg-fuchsia-600 text-white hover:bg-fuchsia-500"
          >
            Add hold
          </button>
        </footer>
      </div>
    </div>
  );
}
